"use client";

import React, { useState } from "react";
import { LucideIcon, ChevronDown, ChevronUp } from "lucide-react";
import { Typography } from "./Typography";
import { Button } from "@/components/forms/Button";
import { Card } from "./Card";
import { cn } from "@/lib/utils/cn";

export interface SectionCardProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string | React.ReactNode;
  headerPrefix?: React.ReactNode;
  icon?: LucideIcon;
  headerAction?: React.ReactNode;
  badge?: React.ReactNode;
  footer?: React.ReactNode;
  variant?: "default" | "bordered" | "elevated";
  padding?: "none" | "sm" | "md" | "lg";
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
}

const variantStyles = {
  default: "",
  bordered: "border border-gray-200",
  elevated: "shadow-lg border-gray-100",
};

const paddingStyles = {
  none: "",
  sm: "p-4",
  md: "p-6",
  lg: "p-8",
};

export function SectionCard({
  children,
  title,
  subtitle,
  headerPrefix,
  icon: Icon,
  headerAction,
  badge,
  footer,
  variant = "default",
  padding = "md",
  className = "",
  headerClassName = "",
  contentClassName = "",
  collapsible = false,
  defaultOpen = true,
}: SectionCardProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const paddingClass = paddingStyles[padding];
  const hasHeader = title || subtitle || Icon || headerAction || badge;

  return (
    <Card
      padding="none"
      className={cn(variantStyles[variant], className)}
    >
      {hasHeader && (
        <div
          className={cn(
            "flex items-center justify-between gap-4 border-b border-gray-200",
            padding === "none" ? "p-6" : paddingClass,
            collapsible && "cursor-pointer hover:bg-gray-50 transition-colors",
            headerClassName,
          )}
          onClick={collapsible ? () => setIsOpen(!isOpen) : undefined}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {Icon && (
              <div className="flex-shrink-0 rounded-xl bg-gradient-to-br from-navigator-primary to-navigator-primary-dark flex items-center justify-center p-2.5">
                <Icon className="w-5 h-5 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              {headerPrefix && (
                <div className="mb-2">{headerPrefix}</div>
              )}
              {title && (
                <Typography variant="headline3" as="h3" truncate>
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="headline5" color="muted" as="div" className="mt-0.5">
                  {typeof subtitle === "string" ? <p>{subtitle}</p> : subtitle}
                </Typography>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {badge && <div>{badge}</div>}
            {headerAction && (
              <div onClick={(e) => e.stopPropagation()}>{headerAction}</div>
            )}
            {collapsible && (
              <Button
                type="button"
                iconOnly
                startIcon={isOpen ? <ChevronUp className="w-4 h-4 text-gray-600" /> : <ChevronDown className="w-4 h-4 text-gray-600" />}
                variant="ghost"
                size="md"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(!isOpen);
                }}
                title={isOpen ? "Collapse" : "Expand"}
                aria-label={isOpen ? "Collapse section" : "Expand section"}
                className="bg-gray-50 border border-gray-200 shadow-sm hover:bg-gray-100 hover:border-gray-300"
              />
            )}
          </div>
        </div>
      )}

      {(!collapsible || isOpen) && (
        <div className={cn(paddingClass, contentClassName)}>{children}</div>
      )}

      {footer && (
        <div
          className={cn(
            "border-t border-gray-200 bg-gray-50 rounded-b-xl",
            padding === "none" ? "p-6" : paddingClass,
          )}
        >
          {footer}
        </div>
      )}
    </Card>
  );
}
